"use client";

import React, { useState } from "react";
import { Instagram, Phone, Mail, Globe, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Image from "next/image";

interface Footer7Props {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  sections?: Array<{
    title: string;
    links: Array<{
      name: string;
      href: string;
      subLinks?: Array<{
        name: string;
        href: string;
      }>;
    }>;
  }>;
  description?: string;
  socialLinks?: Array<{
    icon: React.ReactElement;
    href: string;
    label: string;
  }>;
  copyright?: string;
  phone?: string;
  email?: string;
  web?: {
    url: string;
    label: string;
  };
  address?: string;
}

const defaultSections = [
  {
    title: "Ventilationslösningar",
    links: [
      { name: "Mini-FTX", href: "/mini-ftx" },
      { name: "PAX-fläkt", href: "/pax-flakt" },
      { name: "Tilluftsventiler", href: "/tilluftsventiler" },
    ],
  },
  {
    title: "Kunskap",
    links: [
      { name: "Kunskapsbanken", href: "/kunskapsbank" },
      { name: "Vad är enrumsventilation?", href: "/vad-ar-enrumsventilation" },
    ],
  },
  {
    title: "Hjälp & Kontakt",
    links: [
      { name: "Behöver du hjälp?", href: "/ventilation-installation" },
      { name: "Ring oss: 08-604 02 40", href: "tel:08-604 02 40" },
    ],
  },
];

const defaultSocialLinks = [
  {
    icon: <Instagram className="size-5" />,
    href: "https://www.instagram.com/villaklimat/",
    label: "Instagram",
  },
];

export const Footer7 = ({
  logo = {
    url: "/",
    src: "/enrumsicon.ico",
    alt: "Enrumsventilation logo",
    title: "Enrumsventilation.se",
  },
  sections = defaultSections,
  description = "Enrumsventilation.se är en kunskapssida om ventilation i enskilda rum. Lär dig allt om mini-FTX, PAX-fläktar och tilluftsventiler. Drivs av miniftx.se och villaklimat.se.",
  socialLinks = defaultSocialLinks,
  copyright = "© 2026 Enrumsventilation.se. Alla rättigheter förbehållna.",
  phone = "08-604 02 40",
  email = "info@villaklimat.se",
  web = { url: "https://enrumsventilation.se", label: "enrumsventilation.se" },
  address = "Stockholm, Sverige",
}: Footer7Props) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const toggleDropdown = (title: string) => {
    setOpenDropdown(openDropdown === title ? null : title);
  };

  return (
    <footer className=" bg-background">
      <div className="relative w-full px-6 py-16 mx-auto lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Brand Section */}
          <div className="space-y-6 lg:col-span-4">
            <div className="flex items-center gap-3">
              <a href={logo.url} className="flex items-center">
                <Image
                  src="/Bilder/Logos/new_logo.webp"
                  alt="Enrumsventilation.se logotyp"
                  width={200}
                  height={60}
                  className="h-auto w-[200px]"
                />
              </a>
            </div>

            <p className="max-w-sm leading-relaxed text-muted-foreground">
              {description}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="size-4 text-primary" />
                <span className="font-medium text-foreground">{phone}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="size-4 text-primary" />
                <a
                  href={`mailto:${email}`}
                  className="transition-colors text-foreground hover:text-primary"
                >
                  {email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Globe className="size-4 text-primary" />
                <a
                  href={web.url}
                  target="_blank"
                  rel="noreferrer"
                  className="transition-colors text-foreground hover:text-primary"
                >
                  {web.label}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="size-4 text-primary" />
                <span className="text-foreground">{address}</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 transition-colors rounded-full hover:bg-primary hover:text-primary-foreground"
                  asChild
                >
                  <a href={social.href} aria-label={social.label}>
                    {social.icon}
                  </a>
                </Button>
              ))}
            </div>

            {/* Partners */}
            <div className="pt-4 space-y-2">
              <p className="text-sm font-medium text-foreground">Samarbetspartners:</p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://miniftx.se"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm transition-colors text-muted-foreground hover:text-primary"
                >
                  miniftx.se
                </a>
                <a
                  href="https://villaklimat.se"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm transition-colors text-muted-foreground hover:text-primary"
                >
                  villaklimat.se
                </a>
              </div>
            </div>
          </div>

          {/* Navigation Sections */}
          <div className="grid grid-cols-1 gap-8 lg:col-span-5 md:grid-cols-3">
            {sections.map((section, sectionIdx) => (
              <div key={sectionIdx} className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      {link.subLinks ? (
                        <div className="relative">
                          <button
                            onClick={() =>
                              toggleDropdown(`${sectionIdx}-${linkIdx}`)
                            }
                            className="flex items-center justify-between w-full text-sm transition-colors text-muted-foreground hover:text-primary"
                          >
                            <span>{link.name}</span>
                            <svg
                              className={`w-4 h-4 ml-1 transition-transform ${
                                openDropdown === `${sectionIdx}-${linkIdx}`
                                  ? "rotate-180"
                                  : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {openDropdown === `${sectionIdx}-${linkIdx}` && (
                            <ul className="pl-4 mt-2 space-y-2">
                              {link.subLinks.map((subLink, subLinkIdx) => (
                                <li key={subLinkIdx}>
                                  <a
                                    href={subLink.href}
                                    className="text-xs transition-colors text-muted-foreground hover:text-primary"
                                  >
                                    {subLink.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <a
                          href={link.href}
                          className="text-sm transition-colors text-muted-foreground hover:text-primary"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 mt-16 border-t">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-muted-foreground">{copyright}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer7;
